import type { Dispatch, SetStateAction } from "react";

import toast from "react-hot-toast";

import api from "api";

export type DragFile = { kind: string; type: string };

export type UploadState = { isUploading: boolean; uploadProgress: number };

export type OnCompleteParams = {
  savedName: string;
  fileName: string;
  signedDownloadRequest: string;
};

export const allowedImageTypes = {
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  jpg: "image/jpg",
};
export const allowedImageTypesList = Object.values(allowedImageTypes);

export const allowedVideoTypes = {
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  m2ts: "video/avchd-stream",
  flv: "video/x-flv",
};
export const allowedVideoTypesList = Object.values(allowedVideoTypes);

export const allowedFileTypes = {
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pdf: "application/pdf",
  csv: "text/csv",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  xml: "application/xml",
  xml_text: "text/xml",
  odt: "application/vnd.oasis.opendocument.text",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  odp: "application/vnd.oasis.opendocument.presentation",
  odg: "application/vnd.oasis.opendocument.graphics",
  gif: "image/gif",
  tiff: "image/tiff",
  svg: "image/svg+xml",
  ics: "text/calendar",
  ...allowedImageTypes,
  ...allowedVideoTypes,
};
export const allowedFileTypesList = Object.values(allowedFileTypes);

export const verifyType = (accept?: string, type?: string) => {
  if (!type) {
    return { allowed: false, error: "Unknown file type" };
  }
  if (accept) {
    const acceptList = accept.split(",").map((a) => a.trim());
    if (!acceptList.includes(type)) {
      return { allowed: false, error: `${type} is not an accepted file type` };
    }
  }

  // The only image types we accept are JPEG, PNG, and WebP
  if (type.startsWith("image/")) {
    if (!allowedImageTypesList.includes(type)) {
      return {
        allowed: false,
        error: "Only JPEG, PNG, and WebP images are accepted",
      };
    }
  }
  if (type.startsWith("video/")) {
    if (!allowedVideoTypesList.includes(type)) {
      return {
        allowed: false,
        error: "Only MP4, MOV, AVI, MKV, M2TS, and FLV videos are accepted",
      };
    }
  }
  if (!allowedFileTypesList.includes(type)) {
    return { allowed: false, error: "File type is not supported" };
  }

  return { allowed: true };
};

export const uploadFile = (
  file: File | null,
  setUploadState: Dispatch<SetStateAction<UploadState>> = () => {},
  forType: string,
  forId: string,
  onComplete: (
    savedName: OnCompleteParams["savedName"],
    fileName: OnCompleteParams["fileName"],
    signedDownloadRequest: OnCompleteParams["signedDownloadRequest"],
  ) => void = () => {},
  onError?: (message: string) => void,
  setOpen: Dispatch<SetStateAction<boolean>> = () => {},
) => {
  if (file) {
    setUploadState((prevState) => ({
      ...prevState,
      isUploading: true,
      uploadProgress: 1,
    }));
    const regex = /[^a-zA-Z0-9_.]/g;
    const name = file.name.replace(regex, "-");

    // There are slightly different restrictions on video files than regular files
    const uploadFunction =
      allowedVideoTypesList.includes(file.type) ?
        api.uploads.generateVideoUploadRequest
      : api.uploads.generateFileUploadRequest;

    uploadFunction(
      { forType, forId, name, size: file.size, type: file.type },
      (response) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", response.signedRequest);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            setUploadState((prevState) => ({
              ...prevState,
              uploadProgress: Math.round((e.loaded * 100) / e.total),
            }));
          }
        });
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            setUploadState((prevState) => ({
              ...prevState,
              uploadProgress: 100,
            }));
            if (xhr.status === 200) {
              setTimeout(() => {
                setUploadState((prevState) => ({
                  ...prevState,
                  isUploading: false,
                  uploadProgress: 0,
                }));
                setOpen(false);
                onComplete(
                  response.fileName,
                  name,
                  response.signedDownloadRequest,
                );
              }, 250);
            } else if (onError) {
              onError("Unable to upload file");
            }
          }
        };
        xhr.send(file);
      },
      (err) => {
        setUploadState((prevState) => ({
          ...prevState,
          isUploading: false,
          uploadProgress: 0,
        }));
        if (onError) onError(err.message || "Unable to upload file");
        else toast.error(err.message);
      },
    );
  }
};

export const uploadMultiFiles = (
  uploadFiles: File[],
  setUploadState: Dispatch<SetStateAction<UploadState>> = () => {},
  forType: string,
  forId: string,
  onCompleteMultiple: (files: OnCompleteParams[]) => void = () => {},
  onError?: (message: string) => void,
  setOpen: Dispatch<SetStateAction<boolean>> = () => {},
) => {
  setUploadState((prevState) => ({
    ...prevState,
    isUploading: true,
    uploadProgress: 1,
  }));
  const regex = /[^a-zA-Z0-9_.]/g;
  const renamedFiles = Array.from(uploadFiles).map((file) => {
    const name = file.name.replace(regex, "-");
    return { name, size: file.size, type: file.type, forType, forId };
  });

  api.uploads.generateMultiFileUploadRequest(
    renamedFiles,
    (response) => {
      const { files = [] } = response;
      const uploaders = files.map((file, index) => {
        return new Promise<OnCompleteParams>((resolve, reject) => {
          const { request, type } = file;
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", request.signedRequest);
          xhr.setRequestHeader("Content-Type", type);
          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              setUploadState((prevState) => ({
                ...prevState,
                uploadProgress: Math.round((e.loaded * 100) / e.total),
              }));
            }
          });
          xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
              if (xhr.status === 200) {
                setTimeout(() => {
                  setUploadState((prevState) => ({
                    ...prevState,
                    isUploading: false,
                    uploadProgress: 0,
                  }));
                  resolve({
                    fileName: request.fileName,
                    signedDownloadRequest: request.signedDownloadRequest,
                    savedName: request.fileName,
                  });
                }, 250);
              } else {
                try {
                  const errorResponse = JSON.parse(xhr.responseText);
                  reject(
                    new Error(errorResponse.message || "Unable to upload file"),
                  );
                } catch {
                  reject(new Error("Unable to upload file"));
                }
              }
            }
          };
          xhr.send(uploadFiles[index]);
        });
      });
      Promise.all(uploaders)
        .then((results: OnCompleteParams[]) => {
          setUploadState((prevState) => ({
            ...prevState,
            isUploading: false,
            uploadProgress: 100,
          }));
          setOpen(false);
          onCompleteMultiple(
            results.map((result, index) => {
              const renamedFile = renamedFiles[index];
              return {
                fileSize: renamedFile?.size,
                savedName: result.fileName,
                fileName: renamedFile?.name ?? result.fileName,
                signedDownloadRequest: result.signedDownloadRequest,
              };
            }),
          );
        })
        .catch((err) => {
          setUploadState((prevState) => ({
            ...prevState,
            isUploading: false,
            uploadProgress: 0,
          }));
          if (onError) onError(err.message || "Unable to upload file");
          else toast.error(err.message);
        });
    },
    (err) => {
      setUploadState((prevState) => ({
        ...prevState,
        isUploading: false,
        uploadProgress: 0,
      }));
      if (onError) onError(err.message || "Unable to upload file");
      else toast.error(err.message);
    },
  );
};
